// src/pages/BQMCalendarPage.js - J49 Banquet Manager Calendar
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Badge } from '@mui/material';
import { eventService, authService } from '../services/api';
import { useRoleAccess } from '../hooks/useRoleAccess';
import '../styles/bqm-calendar.css';

const BQMCalendarPage = () => {
  const navigate = useNavigate();
  useRoleAccess(); // Enforce role-based access control
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    customerEmail: '',
    eventTime: '17:00',
    duration: 4,
    guestCount: 50
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventService.getAllEvents();
      // Show all J49 events (BQM-created) AND API-created events
      const allEvents = (response.events || []).filter(event =>
        event.metadata?.isJ49Event || event.apiClientId
      );
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if a date has events
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      const eventDate = new Date(event.startDate || event.date);
      const eventDateStr = eventDate.toISOString().split('T')[0];
      return eventDateStr === dateStr;
    });
  };

  // Custom day component with availability indicators
  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const dayEvents = outsideCurrentMonth ? [] : getEventsForDate(day);
    const eventCount = dayEvents.length;

    // Color scheme: Yellow (1 event), Red (2+ events - fully booked)
    const getDateColor = () => {
      if (eventCount === 0) return null;
      if (eventCount === 1) return { bg: '#FEF3C7', color: '#92400E', hover: '#FDE68A' }; // Yellow
      return { bg: '#FEE2E2', color: '#991B1B', hover: '#FECACA' }; // Red (fully booked)
    };

    const colors = getDateColor();

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={eventCount > 0 ? eventCount : undefined}
        color={eventCount >= 2 ? "error" : "warning"}
      >
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
          sx={{
            ...(colors && {
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: colors.hover,
              },
            }),
          }}
        />
      </Badge>
    );
  };

  const handleDateClick = (date) => {
    const dayEvents = getEventsForDate(date);

    // Check if date is fully booked (2+ events)
    if (dayEvents.length >= 2) {
      setError(`This date is fully booked with ${dayEvents.length} events. Please select another date.`);
      setShowCreateModal(false);

      // Clear error after 4 seconds
      setTimeout(() => setError(''), 4000);
      return;
    }

    setSelectedDate(date);
    setShowCreateModal(true);
    setError('');
    setSuccess('');
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.customerEmail) {
      setError('Customer email is required');
      return;
    }

    try {
      // Generate unique identifiers for J49 events
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const externalEventId = `j49_bqm_${timestamp}_${randomId}`;

      // Create event
      const eventData = {
        name: `Event on ${selectedDate.toLocaleDateString()}`,
        date: selectedDate.toISOString(),
        startDate: selectedDate.toISOString(),
        venue: 'J49 Banquet Hall',
        budget: 0,
        apiClientId: 'j49_internal',
        externalEventId: externalEventId,
        metadata: {
          isJ49Event: true,
          eventTime: formData.eventTime,
          duration: formData.duration,
          guestCount: formData.guestCount
        }
      };

      const newEvent = await eventService.createEvent(eventData);

      // Invite customer
      await eventService.inviteUser(newEvent._id, {
        email: formData.customerEmail,
        role: 'editor'
      });

      setSuccess('Event created and customer invited successfully!');

      // Refresh events
      await fetchEvents();

      // Reset form after 2 seconds
      setTimeout(() => {
        setShowCreateModal(false);
        setFormData({
          customerEmail: '',
          eventTime: '17:00',
          duration: 4,
          guestCount: 50
        });
        setSuccess('');
      }, 2000);

    } catch (error) {
      console.error('Error creating event:', error);
      setError(error.response?.data?.message || 'Failed to create event. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setError('');
    setSuccess('');
    setFormData({
      customerEmail: '',
      eventTime: '17:00',
      duration: 4,
      guestCount: 50
    });
  };

  return (
    <div className="bqm-calendar-page">
      <div className="bqm-header">
        <div className="bqm-branding">
          <h1 className="bqm-title">J49 Banquet Manager</h1>
          <p className="bqm-subtitle">Calendar & Bookings</p>
        </div>
        <div className="bqm-header-actions">
          <button className="home-btn" onClick={() => navigate('/events')}>
            Home
          </button>
          <button className="logout-btn" onClick={() => {
            authService.logout();
            window.location.href = '/login';
          }}>
            Logout
          </button>
        </div>
      </div>

      <div className="bqm-content">
        {/* Error notification for fully booked dates */}
        {error && !showCreateModal && (
          <div className="booking-error-toast">
            <span>⚠️ {error}</span>
          </div>
        )}

        <div className="calendar-section">
          <div className="calendar-container">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateClick}
                loading={loading}
                slots={{
                  day: CustomDay,
                }}
                sx={{
                  width: '100%',
                  height: 'auto',
                  '& .MuiPickersCalendarHeader-root': {
                    paddingLeft: '24px',
                    paddingRight: '8px'
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          <div className="calendar-legend">
            <h3>Availability</h3>
            <div className="legend-item">
              <div className="legend-dot available"></div>
              <span>Available (0 events)</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot partial"></div>
              <span>Partially booked (1 event)</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot full"></div>
              <span>Fully booked (2+ events)</span>
            </div>
          </div>
        </div>

        <div className="upcoming-events">
          <h2>Upcoming Bookings</h2>
          <div className="events-list">
            {events.length === 0 ? (
              <p className="no-events">No bookings yet. Click a date to create one.</p>
            ) : (
              events
                .filter(event => new Date(event.startDate || event.date) >= new Date())
                .sort((a, b) => new Date(a.startDate || a.date) - new Date(b.startDate || b.date))
                .slice(0, 5)
                .map(event => (
                  <div key={event._id} className="event-card">
                    <div className="event-date">
                      {new Date(event.startDate || event.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="event-details">
                      <h4>{event.name}</h4>
                      <p className="event-venue">{event.venue}</p>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content create-booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Booking for {selectedDate?.toLocaleDateString()}</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleCreateEvent} className="modal-body">
              {error && (
                <div className="error-message">{error}</div>
              )}
              {success && (
                <div className="success-message">{success}</div>
              )}

              <div className="form-group">
                <label>Customer Email *</label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  placeholder="customer@example.com"
                  required
                />
                <small>Customer will receive an invitation to manage this event</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Event Time</label>
                  <input
                    type="time"
                    value={formData.eventTime}
                    onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Duration (hours)</label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                  >
                    <option value={2}>2 hours</option>
                    <option value={3}>3 hours</option>
                    <option value={4}>4 hours</option>
                    <option value={5}>5 hours</option>
                    <option value={6}>6 hours</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Estimated Guest Count</label>
                <input
                  type="number"
                  value={formData.guestCount}
                  onChange={(e) => setFormData({ ...formData, guestCount: parseInt(e.target.value) })}
                  min="1"
                  max="500"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Create & Invite Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BQMCalendarPage;
